import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import type { RootState } from "../../redux/store";
import {
  setClient,
  setAccount,
  setClientId,
  setAccountId,
  updateMetadata,
} from "../../redux/metadata/metadataSlice";
import {
  isReadOnlyUser,
  getReadOnlyDropdownOption,
} from "../../redux/user/roleHelpers";
import type { UserRole } from "../../redux/user/userTypes";
import type { AccountOption, ClientOption } from "../../types/optionTypes";
import {
  SELECT_ACCOUNT_PLACEHOLDER,
  SELECT_CLIENT_PLACEHOLDER,
  canAutoSelectTemplateScope,
  isValidClientsData,
} from "./appLayoutHelpers";

type UseAccountClientSelectionParams = {
  clientsState: RootState["clients"];
  userRole: UserRole;
};

type StoredScope = { account?: string; client?: string };

// Scoped per signed-in user so one user's choice never applies to another.
const getScopeStorageKey = () =>
  `templateStudio.selectedScope.${sessionStorage.getItem("UUID") || "default"}`;

const readStoredScope = (): StoredScope => {
  try {
    const raw = localStorage.getItem(getScopeStorageKey());
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (typeof parsed !== "object" || parsed === null) return {};
    const { account, client } = parsed as Record<string, unknown>;
    return {
      account: typeof account === "string" ? account : undefined,
      client: typeof client === "string" ? client : undefined,
    };
  } catch {
    return {};
  }
};

const writeStoredScope = (patch: StoredScope) => {
  try {
    localStorage.setItem(
      getScopeStorageKey(),
      JSON.stringify({ ...readStoredScope(), ...patch }),
    );
  } catch {
    // Storage can be unavailable (private mode or quota); selection still works in memory.
  }
};

export function useAccountClientSelection({
  clientsState,
  userRole,
}: UseAccountClientSelectionParams) {
  const dispatch = useAppDispatch();
  const metadataAccount = useAppSelector((state) => state.metadata.account);
  const metadataClient = useAppSelector((state) => state.metadata.client);
  const clientId = useAppSelector((state) => state.metadata.clientId);
  const metadataCategory = useAppSelector((state) => state.metadata.category);

  const [selectedAccount, setSelectedAccount] = useState<AccountOption | null>(
    null,
  );
  const [selectedClients, setSelectedClients] = useState<ClientOption | null>(
    null,
  );
  const lastAppliedTemplateIntentRef = useRef<string>("");
  const manualAccountSelectionRef = useRef(false);
  const accountAutoSelectedRef = useRef<boolean>(false);
  const clientAutoSelectedRef = useRef<boolean>(false);

  // Memoize unique platforms to avoid repeated computation
  const uniquePlatforms = useMemo(() => {
    if (!isValidClientsData(clientsState.data)) {
      return [];
    }
    return Array.from(new Set(clientsState.data.map((item) => item.platform)));
  }, [clientsState.data]);

  const accounts: AccountOption[] = useMemo(() => {
    if (isReadOnlyUser(userRole)) {
      return [getReadOnlyDropdownOption()];
    }

    if (!isValidClientsData(clientsState.data)) {
      return [SELECT_ACCOUNT_PLACEHOLDER];
    }

    const platformOptions = uniquePlatforms.map((platform) => ({
      id: platform,
      label: platform,
      value: platform,
      title: platform,
    }));

    return [SELECT_ACCOUNT_PLACEHOLDER, ...platformOptions];
  }, [clientsState.data, userRole, uniquePlatforms]);

  // Auto-select account when clientsState finishes loading: the stored account if still
  // available, otherwise the first one (single account, or several for Platform/Client Admins).
  useEffect(() => {
    const hasSingleAccount = accounts.length === 2;
    const hasMultipleAccounts = accounts.length > 2;
    const storedAccount = readStoredScope().account;
    const storedOption = storedAccount
      ? accounts.find((option) => option.value === storedAccount)
      : undefined;
    const canDefaultToFirst =
      hasSingleAccount ||
      (hasMultipleAccounts &&
        (userRole === "TB_PLATFORM_ADMIN" || userRole === "TB_CLIENT_ADMIN"));
    if (
      !accountAutoSelectedRef.current &&
      clientsState.hasFetched &&
      !clientsState.loading &&
      (storedOption || canDefaultToFirst) &&
      !selectedAccount &&
      userRole !== "TB_GENERAL_READONLY" &&
      userRole !== "TB_ECP_ADMIN"
    ) {
      setSelectedAccount(storedOption ?? accounts[1]);
      accountAutoSelectedRef.current = true;
    }
  }, [
    accounts,
    clientsState.hasFetched,
    clientsState.loading,
    selectedAccount,
    userRole,
  ]);

  const clients: ClientOption[] = useMemo(() => {
    if (isReadOnlyUser(userRole)) {
      return [getReadOnlyDropdownOption()];
    }

    if (!isValidClientsData(clientsState.data)) {
      return [SELECT_CLIENT_PLACEHOLDER];
    }

    const hasMultipleAccounts = uniquePlatforms.length > 1;
    const selectedPlatform = selectedAccount?.value;
    const isValidPlatform = selectedPlatform && selectedPlatform !== "";

    if (hasMultipleAccounts && !isValidPlatform) {
      return [SELECT_CLIENT_PLACEHOLDER];
    }

    const filtered = isValidPlatform
      ? clientsState.data.filter((item) => item.platform === selectedPlatform)
      : clientsState.data;

    // Collapse rows by client_id so one logical client ID is treated as one option.
    const uniqueClientRows = Array.from(
      new Map(
        filtered.map((item) => [String(item.client_id), item] as const),
      ).values(),
    );

    const clientOptions = uniqueClientRows.map((item) => ({
      id: String(item.client_id),
      label: item.client_number || String(item.client_id),
      value: item.client_number || String(item.client_id),
      title: item.client_number || String(item.client_id),
    }));

    return [SELECT_CLIENT_PLACEHOLDER, ...clientOptions];
  }, [clientsState.data, selectedAccount, userRole, uniquePlatforms]);

  // Reset selected client whenever account changes, so client selection is always account-scoped.
  useEffect(() => {
    clientAutoSelectedRef.current = false;
    setSelectedClients(null);
  }, [selectedAccount?.value]);

  // Preselect the stored client if available; otherwise the only client, or the first one for Client Admins.
  useEffect(() => {
    const realClientOptions = clients.filter((client) => client.value !== "");
    const isClientAdmin = userRole === "TB_CLIENT_ADMIN";
    const storedClient = readStoredScope().client;
    const storedOption = storedClient
      ? realClientOptions.find((option) => option.value === storedClient)
      : undefined;
    const canDefaultToFirst =
      (accounts.length === 2 || isClientAdmin) &&
      (realClientOptions.length === 1 ||
        (isClientAdmin && realClientOptions.length > 1));

    if (
      !clientAutoSelectedRef.current &&
      !!selectedAccount?.value &&
      (storedOption || canDefaultToFirst) &&
      !selectedClients &&
      userRole !== "TB_GENERAL_READONLY"
    ) {
      setSelectedClients(storedOption ?? realClientOptions[0]);
      clientAutoSelectedRef.current = true;
    }
  }, [accounts, clients, selectedAccount, selectedClients, userRole]);

  // Remember the latest non-empty choices for the next login.
  useEffect(() => {
    if (selectedAccount?.value) writeStoredScope({ account: selectedAccount.value });
  }, [selectedAccount?.value]);

  useEffect(() => {
    if (selectedClients?.value) writeStoredScope({ client: selectedClients.value });
  }, [selectedClients?.value]);

  useEffect(() => {
    if (!clientsState.hasFetched || !clientsState.data?.length) return;
    if (manualAccountSelectionRef.current) {
      manualAccountSelectionRef.current = false;
      lastAppliedTemplateIntentRef.current = "";
      return;
    }
    const clientsData = clientsState.data;

    const normalizedCategory = metadataCategory.trim().toUpperCase();
    const isGlobalTemplate =
      normalizedCategory === "GLOBAL" || normalizedCategory === "OPTUM";
    const desiredAccount = metadataAccount.trim();
    const desiredClient = metadataClient.trim();
    const hasDesiredAccount = !!desiredAccount;
    const hasDesiredClient = !!desiredClient;
    const intentKey = `${normalizedCategory}|${desiredAccount}|${desiredClient}`;

    // No metadata intent to apply.
    if (!hasDesiredAccount && !hasDesiredClient && !isGlobalTemplate) {
      lastAppliedTemplateIntentRef.current = "";
      return;
    }

    if (lastAppliedTemplateIntentRef.current === intentKey) {
      return;
    }

    if (isGlobalTemplate) {
      if (userRole !== "TB_ECP_ADMIN") return;
      // Only treat as explicit Global scope-reset when metadata has no
      // account/client intent. This avoids overriding manual dropdown picks.
      if (!hasDesiredAccount && !hasDesiredClient) {
        if (selectedClients !== null) {
          setSelectedClients(null);
        }
        if (selectedAccount !== null) {
          setSelectedAccount(null);
        }
      }
      lastAppliedTemplateIntentRef.current = intentKey;
      return;
    }

    // Ignore malformed metadata shape (client without account).
    if (!hasDesiredAccount && hasDesiredClient) return;

    if (
      !canAutoSelectTemplateScope(userRole, hasDesiredAccount, hasDesiredClient)
    )
      return;

    const equalsIgnoreCase = (a: string, b: string) =>
      a.trim().toLowerCase() === b.trim().toLowerCase();

    const resolveAccountValue = (rawAccount: string): string | undefined => {
      const byName = accounts.find(
        (option) => option.value && equalsIgnoreCase(option.value, rawAccount),
      );
      if (byName?.value) return byName.value;

      const byId = clientsData.find(
        (item) => String(item.platform_id).trim() === rawAccount.trim(),
      );
      return byId?.platform;
    };

    const resolveClientValue = (
      rawClient: string,
      selectedAccountValue?: string,
    ): string | undefined => {
      const byNumber = clients.find(
        (option) => option.value && equalsIgnoreCase(option.value, rawClient),
      );
      if (byNumber?.value) return byNumber.value;

      const byId = clientsData.find((item) => {
        const idMatches = String(item.client_id).trim() === rawClient.trim();
        if (!idMatches) return false;
        if (!selectedAccountValue) return true;
        return equalsIgnoreCase(item.platform, selectedAccountValue);
      });
      return byId?.client_number;
    };

    if (hasDesiredAccount) {
      const resolvedAccountValue = resolveAccountValue(desiredAccount);
      if (!resolvedAccountValue) return;

      const matchingAccount = accounts.find(
        (option) =>
          option.value && equalsIgnoreCase(option.value, resolvedAccountValue),
      );

      if (!matchingAccount) return;

      if (selectedAccount?.value !== matchingAccount.value) {
        setSelectedAccount(matchingAccount);
        return;
      }

      // Account intent should keep account, but clear any selected client.
      if (!hasDesiredClient) {
        const hasSingleClientOption = clients.length === 2;
        if (!hasSingleClientOption && selectedClients !== null) {
          setSelectedClients(null);
          return;
        }
        lastAppliedTemplateIntentRef.current = intentKey;
        return;
      }
    }

    if (hasDesiredClient) {
      const accountValueForClientMatch = selectedAccount?.value;
      const resolvedClientValue = resolveClientValue(
        desiredClient,
        accountValueForClientMatch,
      );
      if (!resolvedClientValue) return;

      const matchingClient = clients.find(
        (option) =>
          option.value && equalsIgnoreCase(option.value, resolvedClientValue),
      );

      if (!matchingClient) return;

      if (selectedClients?.value !== matchingClient.value) {
        setSelectedClients(matchingClient);
        return;
      }

      lastAppliedTemplateIntentRef.current = intentKey;
    }
  }, [
    accounts,
    clients,
    clientsState.data,
    clientsState.hasFetched,
    metadataCategory,
    metadataAccount,
    metadataClient,
    selectedAccount,
    selectedClients,
    userRole,
  ]);

  useEffect(() => {
    const normalizedCategory = metadataCategory.trim().toUpperCase();
    const isGlobalTemplate =
      normalizedCategory === "GLOBAL" || normalizedCategory === "OPTUM";

    if (isGlobalTemplate && !metadataAccount.trim() && !metadataClient.trim()) {
      return;
    }

    // Validate selected account still exists in accounts list
    if (selectedAccount && accounts.length > 1) {
      const accountStillExists = accounts.some(
        (acc) => acc.value === selectedAccount.value,
      );
      if (!accountStillExists && selectedAccount.value !== "") {
        // Selected account no longer exists, reset to placeholder or auto-select if only one option
        if (accounts.length === 2) {
          setSelectedAccount(accounts[1]);
        } else {
          setSelectedAccount(null);
          accountAutoSelectedRef.current = false; // Reset flag so account can auto-select again
        }
      }
    }
  }, [
    accounts,
    metadataAccount,
    metadataCategory,
    metadataClient,
    selectedAccount,
  ]);

  const handleAccountChange = useCallback(
    (nextAccount: AccountOption) => {
      manualAccountSelectionRef.current = true;
      setSelectedAccount(nextAccount);
      setSelectedClients(null);

      const matchingPlatform = clientsState.data?.find(
        (client) => client.platform === nextAccount?.value,
      );
      dispatch(setAccount({ account: nextAccount?.value || "" }));
      dispatch(
        setAccountId({
          accountId: matchingPlatform
            ? String(matchingPlatform.platform_id)
            : "",
        }),
      );
      dispatch(setClient({ client: "" }));
      dispatch(setClientId({ clientId: "" }));
    },
    [clientsState.data, dispatch],
  );

  const handleClientChange = useCallback(
    (nextClient: ClientOption) => {
      setSelectedClients(nextClient);

      if (!nextClient?.value || !selectedAccount?.value || !clientsState.data) {
        dispatch(updateMetadata({ client: "", clientId: "" }));
        return;
      }

      const matchingClient = clientsState.data.find(
        (client) =>
          client.client_number === nextClient.value &&
          client.platform === selectedAccount.value,
      );
      const nextClientId = matchingClient
        ? String(matchingClient.client_id)
        : "";

      dispatch(
        updateMetadata({ client: nextClient.value, clientId: nextClientId }),
      );
    },
    [clientsState.data, dispatch, selectedAccount?.value],
  );

  // Auto, restored, and template-driven client picks must reach Redux too, since fetches read clientId from there.
  useEffect(() => {
    if (
      !selectedClients?.value ||
      !selectedAccount?.value ||
      !clientsState.data
    ) {
      return;
    }
    const matchingClient = clientsState.data.find(
      (client) =>
        client.client_number === selectedClients.value &&
        client.platform === selectedAccount.value,
    );
    if (!matchingClient) return;

    const nextClientId = String(matchingClient.client_id);
    if (metadataClient === selectedClients.value && clientId === nextClientId) {
      return;
    }
    dispatch(
      updateMetadata({ client: selectedClients.value, clientId: nextClientId }),
    );
  }, [
    selectedClients,
    selectedAccount?.value,
    clientsState.data,
    metadataClient,
    clientId,
    dispatch,
  ]);

  const resetAccountContext = useCallback(() => {
    setSelectedAccount(null);
    setSelectedClients(null);
    accountAutoSelectedRef.current = false;
    clientAutoSelectedRef.current = false;
  }, []);

  return {
    accounts,
    clients,
    selectedAccount,
    selectedClients,
    handleAccountChange,
    handleClientChange,
    resetAccountContext,
  };
}
